import { useCallback, useEffect, useRef, useState } from "react";
import { Shield, Upload, Trash2, AlertCircle, CheckCircle2, Clock, Sparkles, Loader2 } from "lucide-react";
import { adminListPrograms, adminUpload, adminDelete } from "../lib/api";
import { useAuth } from "../lib/auth";

const TYPE_LABEL = {
    kommune: "Bergen kommune",
    fylkesting: "Vestland fylkesting",
    nasjonal: "Nasjonalt program (Stortinget)",
};

const REGION_BY_TYPE = {
    kommune: "Bergen",
    fylkesting: "Vestland",
    nasjonal: "Norge",
};

function periodStatus(period) {
    const m = period?.match(/(\d{4})\s*[–-]\s*(\d{4})/);
    if (!m) return "unknown";
    const start = parseInt(m[1], 10);
    const end = parseInt(m[2], 10);
    const y = new Date().getFullYear();
    if (y >= start && y <= end) return "current";
    if (y > end) return "historical";
    return "upcoming";
}

export default function Admin() {
    const { isAdmin, loading } = useAuth();
    if (loading) {
        return (
            <main data-testid="admin-page-loading" className="mx-auto max-w-md px-6 py-24">
                <div className="font-mono text-sm text-zinc-500">Sjekker session…</div>
            </main>
        );
    }
    if (!isAdmin) return <Login />;
    return <Dashboard />;
}

function Login() {
    const { login } = useAuth();
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(password);
        } catch (err) {
            setError(err?.response?.data?.detail || "Innlogging feilet");
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="admin-login-page" className="mx-auto max-w-md px-6 py-24">
            <div className="border-2 border-zinc-950 bg-white p-8" style={{ boxShadow: "6px 6px 0 0 rgba(9,9,11,1)" }}>
                <Shield className="h-8 w-8 text-[#C8102E]" />
                <h1 className="mt-4 font-heading text-3xl font-black tracking-tighter text-zinc-950">
                    Admin-innlogging
                </h1>
                <p className="mt-2 text-sm text-zinc-600">
                    Kun administrator kan laste opp og slette partiprogrammer.
                </p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <label className="block">
                        <span className="pp-overline">Passord</span>
                        <input
                            data-testid="admin-password-input"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoFocus
                            className="mt-1.5 w-full border-2 border-zinc-300 bg-white px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                            required
                        />
                    </label>
                    {error && (
                        <div
                            data-testid="admin-login-error"
                            className="flex items-center gap-2 border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
                        >
                            <AlertCircle className="h-4 w-4" /> {error}
                        </div>
                    )}
                    <button
                        data-testid="admin-login-submit"
                        type="submit"
                        disabled={loading || !password}
                        className="w-full justify-center inline-flex items-center gap-2 bg-[#C8102E] px-5 py-3 font-medium text-white transition-colors hover:bg-[#A00D24] disabled:opacity-50"
                    >
                        {loading ? "Logger inn…" : "Logg inn"}
                    </button>
                </form>
            </div>
        </main>
    );
}

function Dashboard() {
    const { logout } = useAuth();
    const [programs, setPrograms] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [msg, setMsg] = useState(null);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        period: "",
        type: "kommune",
        file: null,
    });
    const pollTimerRef = useRef(null);

    const load = useCallback(
        () => adminListPrograms().then(setPrograms).catch(() => {}),
        [],
    );
    useEffect(() => {
        load();
    }, [load]);

    // Poll every 3s while any program is still being processed. Stops as soon
    // as everything is either ready or failed.
    useEffect(() => {
        const anyProcessing = programs.some(
            (p) => p.processing_status === "processing",
        );
        if (!anyProcessing) {
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
            return;
        }
        if (pollTimerRef.current) return;
        pollTimerRef.current = setInterval(() => {
            load();
        }, 3000);
        return () => {
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
    }, [programs, load]);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setMsg(null);
        if (!form.file) {
            setError("Velg en PDF-fil");
            return;
        }
        if (!/^\d{4}\s*[-–]\s*\d{4}$/.test(form.period.trim())) {
            setError("Periode må være på formatet ÅÅÅÅ-ÅÅÅÅ, f.eks. 2023-2027");
            return;
        }
        const region = REGION_BY_TYPE[form.type] || "";
        const fd = new FormData();
        fd.append("party", "Rødt");
        fd.append("period", form.period.trim());
        fd.append("type", form.type);
        fd.append("region", region);
        fd.append("file", form.file);
        setUploading(true);
        try {
            const res = await adminUpload(fd);
            setMsg(
                `Filen er lastet opp. Rødt ${res.program.region} ${res.program.period} behandles nå i bakgrunnen – dette tar typisk 30–90 sekunder for store PDF-er.`,
            );
            setForm({ period: "", type: "kommune", file: null });
            document.getElementById("admin-file-input").value = "";
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(detail || "Opplasting feilet");
            if (err?.response?.status === 401) {
                await logout();
            }
        } finally {
            setUploading(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Slette dette programmet?")) return;
        try {
            await adminDelete(id);
            await load();
        } catch (err) {
            setError(err?.response?.data?.detail || "Sletting feilet");
        }
    };

    // Sort programs newest period first, current period before historical
    const sortedPrograms = [...programs].sort((a, b) => {
        const ay = parseInt((a.period || "").match(/\d{4}/)?.[0] || 0, 10);
        const by = parseInt((b.period || "").match(/\d{4}/)?.[0] || 0, 10);
        return by - ay;
    });

    return (
        <main data-testid="admin-dashboard" className="mx-auto max-w-6xl px-6 py-12 md:px-12">
            <div className="pp-overline mb-2">Administrator · Rødt</div>
            <h1 className="font-heading text-4xl font-black tracking-tighter text-zinc-950 md:text-5xl">
                Last opp partiprogram
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-zinc-600">
                Last opp programmer for Bergen kommune (kommunestyrevalget),
                Vestland fylkesting (fylkestingsvalget) eller Rødt sitt
                nasjonale program (stortingsvalget). Du kan også laste opp
                historiske programmer fra tidligere perioder.
            </p>

            <section className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-12">
                <form
                    onSubmit={submit}
                    data-testid="admin-upload-form"
                    className="border border-zinc-200 bg-white p-6 md:col-span-7"
                >
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Field label="Nivå *">
                            <select
                                data-testid="admin-type-select"
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                                className="w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-klein focus:outline-none"
                            >
                                <option value="kommune">Bergen kommune</option>
                                <option value="fylkesting">Vestland fylkesting</option>
                                <option value="nasjonal">Nasjonalt program (Stortinget)</option>
                            </select>
                        </Field>
                        <Field label="Periode * (f.eks. 2023-2027)">
                            <input
                                data-testid="admin-period-input"
                                required
                                type="text"
                                value={form.period}
                                onChange={(e) => setForm({ ...form, period: e.target.value })}
                                placeholder="2023-2027"
                                className="w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-klein focus:outline-none"
                            />
                        </Field>
                    </div>
                    <Field label="PDF-fil *" className="mt-4">
                        <input
                            id="admin-file-input"
                            data-testid="admin-file-input"
                            required
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={(e) =>
                                setForm({ ...form, file: e.target.files?.[0] || null })
                            }
                            className="block w-full border border-dashed border-zinc-400 bg-zinc-50 px-3 py-3 text-sm file:mr-3 file:border-0 file:bg-zinc-950 file:px-3 file:py-1.5 file:font-medium file:text-white hover:file:bg-zinc-800"
                        />
                    </Field>
                    {error && (
                        <div
                            data-testid="admin-upload-error"
                            className="mt-4 flex items-center gap-2 border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
                        >
                            <AlertCircle className="h-4 w-4" /> {error}
                        </div>
                    )}
                    {msg && (
                        <div
                            data-testid="admin-upload-success"
                            className="mt-4 flex items-center gap-2 border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
                        >
                            <CheckCircle2 className="h-4 w-4" /> {msg}
                        </div>
                    )}
                    <button
                        data-testid="admin-upload-submit"
                        type="submit"
                        disabled={uploading}
                        className="mt-6 w-full justify-center inline-flex items-center gap-2 bg-[#C8102E] px-5 py-3 font-medium text-white transition-colors hover:bg-[#A00D24] disabled:opacity-50"
                    >
                        <Upload className="h-4 w-4" />
                        {uploading ? "Laster opp…" : "Last opp PDF"}
                    </button>
                    {uploading && (
                        <p className="mt-2 text-xs text-zinc-500">
                            Sender filen til serveren. Selve AI-analysen
                            kjører i bakgrunnen etterpå.
                        </p>
                    )}
                </form>

                <aside className="md:col-span-5">
                    <h2 className="pp-overline mb-3">
                        Opplastede programmer ({programs.length})
                    </h2>
                    {programs.length === 0 ? (
                        <p className="text-sm text-zinc-500">Ingen opplastet ennå.</p>
                    ) : (
                        <ul className="space-y-2">
                            {sortedPrograms.map((p) => {
                                const status = periodStatus(p.period);
                                const procStatus = p.processing_status || "ready";
                                return (
                                    <li
                                        key={p.id}
                                        data-testid={`admin-program-${p.id}`}
                                        className="flex items-start justify-between gap-2 border border-zinc-200 bg-white px-3 py-2.5"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-heading text-sm font-bold text-zinc-950 truncate">
                                                {TYPE_LABEL[p.type] || p.type}
                                            </div>
                                            <div className="mt-0.5 flex items-center flex-wrap gap-1.5 text-xs text-zinc-500">
                                                <span>{p.period}</span>
                                                {procStatus === "processing" && (
                                                    <span
                                                        data-testid={`admin-program-status-processing-${p.id}`}
                                                        className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1.5 py-0.5 text-amber-800 border border-amber-200"
                                                    >
                                                        <Loader2 className="h-3 w-3 animate-spin" /> behandles
                                                    </span>
                                                )}
                                                {procStatus === "failed" && (
                                                    <span
                                                        data-testid={`admin-program-status-failed-${p.id}`}
                                                        className="inline-flex items-center gap-0.5 rounded bg-red-50 px-1.5 py-0.5 text-red-800 border border-red-200"
                                                        title={p.processing_error || "Ukjent feil"}
                                                    >
                                                        <AlertCircle className="h-3 w-3" /> feilet
                                                    </span>
                                                )}
                                                {procStatus === "ready" && status === "current" && (
                                                    <span className="inline-flex items-center gap-0.5 text-emerald-700">
                                                        <Sparkles className="h-3 w-3" /> gjeldende
                                                    </span>
                                                )}
                                                {procStatus === "ready" && status === "historical" && (
                                                    <span className="inline-flex items-center gap-0.5 text-zinc-500">
                                                        <Clock className="h-3 w-3" /> historisk
                                                    </span>
                                                )}
                                            </div>
                                            {procStatus === "failed" && p.processing_error && (
                                                <div className="mt-1 text-xs text-red-700 break-words">
                                                    {p.processing_error}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            data-testid={`admin-delete-${p.id}`}
                                            onClick={() => remove(p.id)}
                                            className="shrink-0 p-1.5 text-zinc-400 hover:text-red-600"
                                            title="Slett"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </aside>
            </section>
        </main>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="pp-overline">{label}</span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}
